import type {
  AssignSettlementPaymentDto,
  Payment,
  SettlementBatchDetailsDto,
  SettlementBatchListItemDto,
  SettlementCashFlowOverviewDto,
  SettlementDetailLineDto,
} from '@carvia/ros-client-types';
import type { PaginatedResponse } from '@/entities/pagination/paginated-response';
import { Service } from './http-service';

class SettlementService extends Service {
  async getCashFlowOverview(legalEntityId?: string) {
    const res = await this.httpClient.get<SettlementCashFlowOverviewDto>(
      `${this.endpoint}/cash-flow`,
      { params: { legalEntityId } },
    );
    return res.data;
  }

  async getProcessedBatches(params: { page: number; limit: number; legalEntityId?: string }) {
    const res = await this.httpClient.get<PaginatedResponse<SettlementBatchListItemDto>>(
      `${this.endpoint}/batches`,
      { params },
    );
    return res.data;
  }

  async getProcessedBatch(batchId: string) {
    const res = await this.httpClient.get<SettlementBatchListItemDto>(
      `${this.endpoint}/batches/${batchId}`,
    );
    return res.data;
  }

  async getUnsettledPayments(params: {
    page: number;
    limit: number;
    q?: string;
    legalEntityId?: string;
  }) {
    const res = await this.httpClient.get<PaginatedResponse<Payment>>(
      `${this.endpoint}/unsettled-payments`,
      { params: { ...params, q: params.q || undefined } },
    );
    return res.data;
  }

  async getBatchDetails(batchId: string) {
    const res = await this.httpClient.get<SettlementBatchDetailsDto>(
      `${this.endpoint}/batches/${batchId}/details`,
    );
    return res.data;
  }

  async getPaymentCandidates(params: {
    detailId: string;
    page: number;
    limit: number;
    q?: string;
  }) {
    const res = await this.httpClient.get<PaginatedResponse<Payment>>(
      `${this.endpoint}/payment-candidates`,
      {
        params: {
          detailId: params.detailId,
          page: params.page,
          limit: params.limit,
          q: params.q || undefined,
        },
      },
    );
    return res.data;
  }

  async assignPayment(detailId: string, paymentId: string) {
    const body: AssignSettlementPaymentDto = { paymentId };
    const res = await this.httpClient.post<SettlementDetailLineDto>(
      `${this.endpoint}/details/${detailId}/assign`,
      body,
    );
    return res.data;
  }
}

export const settlementService = new SettlementService('/settlement');
